import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { GridWidgetWrapper } from "@swiggy-private/dls-react-mxn/dist/WidgetController";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { ResponseContext } from "@swiggy-private/portal-dineout-context";
import { useTheme } from "styled-components";
import { CROSS_SELL_BACKGROUND } from "./constants";
import {
    buildCrossSellAnalyticsParams,
    buildCrossSellGridWidgetProps,
    buildCrossSellWidgetImpressionEventPayload,
    buildDishMap,
    resolveCrossSellDisplayItems,
} from "./helpers";
import {
    CrossSellBreakoutRow,
    CrossSellClipLayer,
    CrossSellOuterGrid,
    StyledTopHandle,
    StyledTopHandleArrow,
} from "./styled";
import type { CrossSellContentProps } from "./types";

export const CrossSellContent = ({
    itemId,
    isLeaving,
    onCrossSellShown,
    impressionAnalytics,
}: CrossSellContentProps) => {
    const theme = useTheme();
    const { state } = useContext(ResponseContext);
    const colorMode = theme.colorMode ?? "light";
    const resolvedBg = theme.colors[colorMode][CROSS_SELL_BACKGROUND] ?? CROSS_SELL_BACKGROUND;

    const [entered, setEntered] = useState(false);
    useEffect(() => {
        const raf = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(raf);
    }, []);

    const gridRows = entered && !isLeaving ? "1fr" : "0fr";

    const crossSellConfig = state.crossSell?.config;
    const localCart = state.cart?.localCart;
    const menuPreference = state.menu.preference ?? "";
    const menuAppliedFilters = state.menu.appliedFilters;
    const menuInitialData = state.menu.initialData as IPLWidgetControllerCards[];
    const pinStatus = state.pin?.data?.status ?? "";

    const cartItemIdsAtOpen = state.crossSell?.entries?.find(
        (e) => e.itemId === itemId
    )?.cartItemIdsAtOpen;

    const dishMap = useMemo(() => buildDishMap(menuInitialData), [menuInitialData]);

    const crossSellAnalyticsParams = useMemo(
        () => buildCrossSellAnalyticsParams(itemId, menuPreference, menuAppliedFilters),
        [itemId, menuPreference, menuAppliedFilters]
    );

    const displayItems = useMemo(
        () =>
            resolveCrossSellDisplayItems({
                itemId,
                dishMap,
                crossSellConfig,
                preference: menuPreference,
                appliedFilters: menuAppliedFilters,
                cartItemIdsAtOpen,
            }),
        [itemId, dishMap, crossSellConfig, menuPreference, menuAppliedFilters, cartItemIdsAtOpen]
    );

    const hasItems = !isEmpty(displayItems);

    const gridWidgetProps = useMemo(
        () =>
            buildCrossSellGridWidgetProps({
                displayItems,
                localCart,
                crossSellConfig,
            }),
        [displayItems, localCart, crossSellConfig]
    );

    const crossSellWidgetImpressionSentRef = useRef(false);

    useEffect(() => {
        crossSellWidgetImpressionSentRef.current = false;
    }, [itemId]);

    useEffect(() => {
        if (!hasItems || isLeaving) return;

        onCrossSellShown?.(itemId);

        if (!impressionAnalytics || crossSellWidgetImpressionSentRef.current) return;
        crossSellWidgetImpressionSentRef.current = true;

        impressionAnalytics(
            buildCrossSellWidgetImpressionEventPayload({
                crossSellAnalytics: crossSellAnalyticsParams,
                dishMap,
                displayItems,
                localCart,
                menuInitialData,
                pinStatus,
            })
        );
    }, [
        hasItems,
        isLeaving,
        itemId,
        onCrossSellShown,
        impressionAnalytics,
        crossSellAnalyticsParams,
        dishMap,
        displayItems,
        localCart,
        menuInitialData,
        pinStatus,
    ]);

    if (!hasItems) {
        return null;
    }

    return (
        <CrossSellOuterGrid $gridRows={gridRows}>
            <CrossSellClipLayer>
                <CrossSellBreakoutRow>
                    <StyledTopHandle>
                        <StyledTopHandleArrow $color={resolvedBg} />
                    </StyledTopHandle>
                    <GridWidgetWrapper {...gridWidgetProps} />
                </CrossSellBreakoutRow>
            </CrossSellClipLayer>
        </CrossSellOuterGrid>
    );
};
